import React, { useCallback, useEffect, useState } from 'react';
import { useAnalyticsPackage, PackageItem } from '../context/AnalyticsPackageContext';
import { useApi } from '../hooks/useApi';

interface LensListDto {
  id: number;
  name: string;
  isSystem: boolean;
  entityScope: string | null;
  activeLensId: number | null;
  activeLensName: string | null;
  items: ApiItem[];
}

interface ApiItem {
  entityType: string;
  entityId: number;
  entityName: string;
  chartName: string;
  chartTitle: string;
  propertyType: string | null;
  buildingCategoryId: number | null;
  yearStart: number | null;
  yearEnd: number | null;
  subareaId: number | null;
  subareaName: string | null;
  sortKey: string | null;
  hiddenSeries?: string[];
}

interface MarketOption { id: number; name: string; }
interface NeighborhoodOption { id: number; name: string; }

function apiItemToPackageItem(i: ApiItem): PackageItem {
  const key = [
    i.entityType, i.entityId, i.chartName,
    i.propertyType ?? '', i.buildingCategoryId ?? '',
    i.yearStart ?? '', i.yearEnd ?? '',
    i.subareaId ?? '', i.sortKey ?? '',
  ].join('|');
  return {
    key,
    entityType: i.entityType as PackageItem['entityType'],
    entityId: i.entityId,
    entityName: i.entityName,
    chartName: i.chartName,
    chartTitle: i.chartTitle,
    propertyType: i.propertyType,
    buildingCategoryId: i.buildingCategoryId,
    yearStart: i.yearStart,
    yearEnd: i.yearEnd,
    subareaId: i.subareaId,
    subareaName: i.subareaName,
    sortOrder: i.sortKey,
    hiddenSeries: i.hiddenSeries?.length ? i.hiddenSeries : undefined,
  };
}

function remapItems(items: ApiItem[], entityType: string, entityId: number, entityName: string): PackageItem[] {
  return items.map(i => apiItemToPackageItem({ ...i, entityType, entityId, entityName }));
}

export function MyLensesPanel() {
  const { items: currentItems, myLensesPanelOpen, setMyLensesPanelOpen, setItemsFromLoad } = useAnalyticsPackage();
  const { authFetch, isAuthenticated } = useApi();

  const [lenses, setLenses]             = useState<LensListDto[]>([]);
  const [loading, setLoading]           = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Replace warning + pending load
  const [pendingLens, setPendingLens]         = useState<LensListDto | null>(null);
  const [showReplaceWarning, setShowReplaceWarning] = useState(false);

  // Entity picker state
  const [pickerStep, setPickerStep]       = useState<'market' | 'neighborhood' | null>(null);
  const [markets, setMarkets]             = useState<MarketOption[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<NeighborhoodOption[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const fetchLenses = useCallback(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    authFetch('/api/lenses')
      .then(r => r.json())
      .then((data: LensListDto[]) => setLenses(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated, authFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (myLensesPanelOpen) fetchLenses();
  }, [myLensesPanelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setMyLensesPanelOpen(false);
    setShowReplaceWarning(false);
    setPendingLens(null);
    setPickerStep(null);
    setDeleteConfirmId(null);
  };

  useEffect(() => {
    if (!myLensesPanelOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [myLensesPanelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: user clicks › on a lens
  const handleLoadClick = (lens: LensListDto) => {
    setPendingLens(lens);
    if (currentItems.length > 0) {
      setShowReplaceWarning(true);
    } else {
      proceedWithLoad(lens);
    }
  };

  // Step 2: after replace confirmed (or no replace needed)
  const proceedWithLoad = (lens: LensListDto) => {
    setShowReplaceWarning(false);
    if (lens.isSystem && lens.entityScope) {
      // Need entity picker
      setPickerLoading(true);
      authFetch('/api/markets')
        .then(r => r.json())
        .then((data: any[]) => {
          setMarkets(data.filter(m => m.hasAccess).map(m => ({ id: m.id, name: m.name })));
          setPickerStep('market');
        })
        .catch(() => {})
        .finally(() => setPickerLoading(false));
    } else {
      // Load directly
      loadLens(lens, null, null, null);
    }
  };

  // Step 3: market picked (for scope=market: load; for scope=neighborhood: fetch neighborhoods)
  const handleMarketPick = (market: MarketOption) => {
    if (!pendingLens) return;
    if (pendingLens.entityScope === 'market') {
      loadLens(pendingLens, 'market', market.id, market.name);
    } else {
      // scope = neighborhood, need to pick a neighborhood
      setPickerLoading(true);
      authFetch(`/api/markets/${market.id}/neighborhoods`)
        .then(r => r.json())
        .then((data: any[]) => {
          setNeighborhoods(data.filter(n => n.hasAccess).map(n => ({ id: n.id, name: n.name })));
          setPickerStep('neighborhood');
        })
        .catch(() => {})
        .finally(() => setPickerLoading(false));
    }
  };

  // Step 4: neighborhood picked
  const handleNeighborhoodPick = (nh: NeighborhoodOption) => {
    if (!pendingLens) return;
    loadLens(pendingLens, 'neighborhood', nh.id, nh.name);
  };

  const loadLens = (lens: LensListDto, entityType: string | null, entityId: number | null, entityName: string | null) => {
    let packageItems: PackageItem[];
    if (entityType && entityId !== null && entityName !== null) {
      packageItems = remapItems(lens.items, entityType, entityId, entityName);
    } else {
      packageItems = lens.items.map(apiItemToPackageItem);
    }
    setItemsFromLoad(packageItems, lens.id, lens.name);
    close();
  };

  const handleDelete = async (id: number) => {
    await authFetch(`/api/lenses/${id}`, { method: 'DELETE' }).catch(() => {});
    setDeleteConfirmId(null);
    setLenses(prev => prev.filter(l => l.id !== id));
  };

  const systemLenses = lenses.filter(l => l.isSystem);
  const userLenses   = lenses.filter(l => !l.isSystem);

  const btnStyle: React.CSSProperties = {
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: "'Montserrat', sans-serif",
  };

  const pickerSubPanelOpen = pickerStep !== null;

  return (
    <>
      {myLensesPanelOpen && (
        <div
          onClick={close}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1290 }}
        />
      )}

      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
        background: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        zIndex: 1300,
        transform: myLensesPanelOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        fontFamily: "'Montserrat', sans-serif",
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #eee', flexShrink: 0,
        }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Lenses</span>
          <button onClick={close} style={{ ...btnStyle, fontSize: 20, color: '#888' }}>✕</button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.5rem' }}>
          {loading ? (
            <div style={{ color: '#999', fontSize: 14, marginTop: '1rem' }}>Loading…</div>
          ) : (
            <>
              {systemLenses.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                    Templates
                  </div>
                  {systemLenses.map(lens => (
                    <LensRow
                      key={lens.id}
                      lens={lens}
                      deleteConfirmId={deleteConfirmId}
                      setDeleteConfirmId={setDeleteConfirmId}
                      onLoad={handleLoadClick}
                      onDelete={handleDelete}
                      canDelete={false}
                    />
                  ))}
                </div>
              )}

              {userLenses.length > 0 && (
                <div>
                  {systemLenses.length > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                      Saved
                    </div>
                  )}
                  {userLenses.map(lens => (
                    <LensRow
                      key={lens.id}
                      lens={lens}
                      deleteConfirmId={deleteConfirmId}
                      setDeleteConfirmId={setDeleteConfirmId}
                      onLoad={handleLoadClick}
                      onDelete={handleDelete}
                      canDelete={true}
                    />
                  ))}
                </div>
              )}

              {lenses.length === 0 && (
                <div style={{ color: '#999', fontSize: 14, marginTop: '1rem' }}>
                  No saved lenses yet. Use the "Save Lens…" button in the Lens panel to save your current insights.
                </div>
              )}
            </>
          )}
        </div>

        {/* Replace warning overlay */}
        {showReplaceWarning && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.95)',
            zIndex: 20, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', padding: '2rem 1.5rem', gap: '1.25rem',
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#000' }}>Replace current Lens?</div>
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
              You have existing content in your current Lens. Loading a saved Lens will replace that content.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => { if (pendingLens) proceedWithLoad(pendingLens); }}
                style={{
                  padding: '0.65rem', fontSize: 14, fontWeight: 600,
                  background: '#ba9394', color: '#fff', border: 'none', borderRadius: 6,
                  cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Load Anyway
              </button>
              <button
                onClick={() => { setShowReplaceWarning(false); setPendingLens(null); }}
                style={{
                  padding: '0.5rem', fontSize: 13, fontWeight: 500,
                  background: 'none', color: '#999', border: '1px solid #e0e0e0', borderRadius: 6,
                  cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Entity picker sub-panel */}
        <div style={{
          position: 'absolute', inset: 0, background: '#fff',
          transform: pickerSubPanelOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.2s ease',
          display: 'flex', flexDirection: 'column',
          zIndex: 30,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #eee', flexShrink: 0,
          }}>
            <button
              onClick={() => setPickerStep(null)}
              style={{ ...btnStyle, fontSize: 18, color: '#555', lineHeight: 1, padding: 0 }}
            >
              ‹
            </button>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {pickerStep === 'neighborhood' ? `Choose Neighborhood` : 'Choose Market'}
            </span>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.5rem' }}>
            {pickerLoading ? (
              <div style={{ color: '#999', fontSize: 14, marginTop: '1rem' }}>Loading…</div>
            ) : pickerStep === 'market' ? (
              markets.length === 0 ? (
                <div style={{ color: '#999', fontSize: 14, marginTop: '1rem' }}>No accessible markets.</div>
              ) : markets.map(m => (
                <button
                  key={m.id}
                  onClick={() => handleMarketPick(m)}
                  style={{
                    ...btnStyle, display: 'block', width: '100%', textAlign: 'left',
                    padding: '0.75rem 0', fontSize: 14, color: '#000',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {m.name} <span style={{ color: '#ba9394', float: 'right' }}>›</span>
                </button>
              ))
            ) : pickerStep === 'neighborhood' ? (
              neighborhoods.length === 0 ? (
                <div style={{ color: '#999', fontSize: 14, marginTop: '1rem' }}>No accessible neighborhoods in this market.</div>
              ) : neighborhoods.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleNeighborhoodPick(n)}
                  style={{
                    ...btnStyle, display: 'block', width: '100%', textAlign: 'left',
                    padding: '0.75rem 0', fontSize: 14, color: '#000',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {n.name} <span style={{ color: '#ba9394', float: 'right' }}>›</span>
                </button>
              ))
            ) : null}
          </div>
        </div>

      </div>
    </>
  );
}

interface LensRowProps {
  lens: LensListDto;
  deleteConfirmId: number | null;
  setDeleteConfirmId: (id: number | null) => void;
  onLoad: (lens: LensListDto) => void;
  onDelete: (id: number) => void;
  canDelete: boolean;
}

function LensRow({ lens, deleteConfirmId, setDeleteConfirmId, onLoad, onDelete, canDelete }: LensRowProps) {
  const confirming = deleteConfirmId === lens.id;

  return (
    <div
      onClick={() => { if (!confirming) onLoad(lens); }}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.65rem 0', borderBottom: '1px solid #f0f0f0',
        cursor: confirming ? 'default' : 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {lens.name}
        </div>
        <div style={{ fontSize: 11, color: '#999' }}>
          {lens.items.length} insight{lens.items.length !== 1 ? 's' : ''}
          {lens.entityScope ? ` · ${lens.entityScope}` : ''}
        </div>
      </div>

      {canDelete && !confirming && (
        <button
          onClick={e => { e.stopPropagation(); setDeleteConfirmId(lens.id); }}
          title="Delete"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 14, padding: '0 0.25rem', lineHeight: 1, fontFamily: "'Montserrat', sans-serif" }}
        >
          ✕
        </button>
      )}

      {confirming && (
        <>
          <span style={{ fontSize: 12, color: '#888' }}>Delete?</span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(lens.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(214,10,34)', fontSize: 12, fontWeight: 600, padding: '0 0.25rem', fontFamily: "'Montserrat', sans-serif" }}
          >
            Yes
          </button>
          <button
            onClick={e => { e.stopPropagation(); setDeleteConfirmId(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 12, padding: '0 0.25rem', fontFamily: "'Montserrat', sans-serif" }}
          >
            No
          </button>
        </>
      )}
    </div>
  );
}
